/**
 * Socket Manager — Singleton orchestrator for Phoenix WebSocket connections.
 * Delegates channel logic to dedicated submodules.
 */

import type { Channel } from 'phoenix';
import { useAuthStore } from '@/modules/auth/store';
import { socketLogger as logger } from '../logger';
import {
  connectSocket,
  disconnectSocket,
  reconnectSocketWithFreshToken,
  type SocketManagerState,
} from './connectionLifecycle';
import { registerCustomizationChangeNotifier } from './customization-events';
import {
  sendTypingDebounced as sendTypingDebouncedImpl,
  sendReaction as sendReactionImpl,
  peekConversationsPresence as peekPresenceImpl,
} from './socketUtils';
import {
  joinUserChannel as joinUserChannelImpl,
  leaveUserChannel as leaveUserChannelImpl,
} from './userChannel';
import {
  joinPresenceLobby as joinPresenceLobbyImpl,
  leavePresenceLobby as leavePresenceLobbyImpl,
  isFriendOnline as isFriendOnlineImpl,
  getOnlineFriends as getOnlineFriendsImpl,
  getOnlineUsers as getOnlineUsersImpl,
  isUserOnline as isUserOnlineImpl,
  getAllOnlineStatuses as getAllOnlineStatusesImpl,
  getFriendCustomization as getFriendCustomizationImpl,
} from './presenceManager';
import {
  joinConversation as joinConversationImpl,
  leaveConversation as leaveConversationImpl,
} from './conversationChannel';
import {
  joinGroupChannel as joinGroupChannelImpl,
  leaveGroupChannel as leaveGroupChannelImpl,
} from './groupChannel';
import {
  joinForum as joinForumFT,
  leaveForum as leaveForumFT,
  subscribeToForum as subscribeToForumFT,
  unsubscribeFromForum as unsubscribeFromForumFT,
  joinThread as joinThreadFT,
  leaveThread as leaveThreadFT,
  voteOnThread as voteOnThreadFT,
  voteOnComment as voteOnCommentFT,
  sendComment as sendCommentFT,
  sendThreadTyping as sendThreadTypingFT,
  getThreadViewers as getThreadViewersFT,
} from './socket-manager-forum-thread';
import type {
  ForumChannelCallbacks,
  ThreadChannelCallbacks,
  ThreadVotePayload,
  CommentVotePayload,
  ThreadViewerPayload,
} from './socket-manager-forum-thread';

/**
 *
 */
export class SocketManager {
  private state: SocketManagerState = {
    socket: null,
    channels: new Map(),
    presences: new Map(),
    onlineUsers: new Map(),
    reconnectTimer: null,
    connectionPromise: null,
    channelHandlersSetUp: new Set(),
    lastJoinAttempts: new Map(),
    forumCallbacks: new Map(),
    threadCallbacks: new Map(),
    sessionId: null,
    lastSequence: 0,
    reconnectAttempts: 0,
    connectedToken: null,
    credentialReconnectInProgress: false,
  };
  private statusListeners = new Set<(cId: string, uId: string, online: boolean) => void>();
  private readonly JOIN_DEBOUNCE_MS = 1000;
  private peekTimeouts = new Set<ReturnType<typeof setTimeout>>();
  private requiredUserChannelId: string | null = null;
  private requiredConversationIds = new Set<string>();

  constructor() {
    this.state.onConnected = () => this.restoreRequiredRealtimeSubscriptions();

    registerCustomizationChangeNotifier(() => {
      this.notifyCustomizationChanged();
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        logger.info('Browser online event - resetting socket circuit breaker');
        this.state.reconnectAttempts = 0;
        if (!this.isConnected() && useAuthStore.getState().token) {
          this.connect();
        }
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.state.reconnectAttempts = 0;
          if (!this.isConnected() && useAuthStore.getState().token) {
            this.connect();
          }
        }
      });
    }
  }

  // State accessors — socket is public for collaborative editor access
  /**
   *
   */
  get socket() {
    return this.state.socket;
  }
  private get channels() {
    return this.state.channels;
  }
  private get presences() {
    return this.state.presences;
  }
  private get onlineUsers() {
    return this.state.onlineUsers;
  }
  private get channelHandlersSetUp() {
    return this.state.channelHandlersSetUp;
  }
  private get lastJoinAttempts() {
    return this.state.lastJoinAttempts;
  }

  /**
   *
   */
  connect(): Promise<void> {
    return connectSocket(this.state);
  }

  /**
   *
   */
  disconnect() {
    this.requiredUserChannelId = null;
    this.requiredConversationIds.clear();
    disconnectSocket(this.state);
  }

  /**
   *
   */
  async reconnectWithNewToken(): Promise<void> {
    logger.log('Reconnecting socket with new token...');
    await reconnectSocketWithFreshToken(this.state);
  }

  /**
   *
   */
  isConnected(): boolean {
    return this.socket?.isConnected() ?? false;
  }
  /**
   *
   */
  getSocket() {
    return this.socket;
  }

  /**
   *
   */
  joinUserChannel(userId: string): Channel | null {
    this.requiredUserChannelId = userId;
    return joinUserChannelImpl(
      this.socket,
      userId,
      this.channels,
      this.onlineUsers,
      this.notifyStatusChange.bind(this),
      this.state
    );
  }

  /**
   *
   */
  leaveUserChannel(userId: string) {
    if (this.requiredUserChannelId === userId) {
      this.requiredUserChannelId = null;
    }
    leaveUserChannelImpl(userId, this.channels);
  }

  /**
   *
   */
  joinPresenceLobby(): Channel | null {
    return joinPresenceLobbyImpl(
      this.socket,
      this.channels,
      this.presences,
      this.onlineUsers,
      this.notifyStatusChange.bind(this)
    );
  }

  /**
   *
   */
  leavePresenceLobby() {
    leavePresenceLobbyImpl(this.channels, this.presences, this.onlineUsers);
  }

  /**
   *
   */
  isFriendOnline(userId: string): boolean {
    return isFriendOnlineImpl(userId, this.onlineUsers);
  }

  /**
   *
   */
  getOnlineFriends(): string[] {
    return getOnlineFriendsImpl(this.onlineUsers);
  }

  /**
   *
   */
  onStatusChange(cb: (cId: string, uId: string, online: boolean) => void): () => void {
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  }

  private notifyStatusChange(cId: string, uId: string, online: boolean) {
    this.statusListeners.forEach((cb) => cb(cId, uId, online));
  }

  private restoreRequiredRealtimeSubscriptions(): void {
    if (this.requiredUserChannelId && !this.channels.has(`user:${this.requiredUserChannelId}`)) {
      this.joinUserChannel(this.requiredUserChannelId);
    }

    this.requiredConversationIds.forEach((conversationId) => {
      if (!this.channels.has(`conversation:${conversationId}`)) {
        this.joinConversation(conversationId);
      }
    });
  }

  /**
   *
   */
  getOnlineUsers(conversationId: string): string[] {
    return getOnlineUsersImpl(conversationId, this.onlineUsers);
  }

  /**
   *
   */
  isUserOnline(conversationId: string, userId: string): boolean {
    return isUserOnlineImpl(conversationId, userId, this.onlineUsers);
  }

  /**
   *
   */
  getAllOnlineStatuses(): Map<string, Set<string>> {
    return getAllOnlineStatusesImpl(this.onlineUsers);
  }

  /**
   *
   */
  getFriendCustomization(userId: string) {
    return getFriendCustomizationImpl(userId);
  }

  /**
   *
   */
  notifyCustomizationChanged(): void {
    const topic = 'presence:lobby';
    const channel = this.channels.get(topic);
    if (channel) {
      channel.push('customization_changed', {});
    }
  }

  /**
   *
   */
  joinConversation(conversationId: string): Channel | null {
    this.requiredConversationIds.add(conversationId);
    return joinConversationImpl(
      this.socket,
      conversationId,
      this.channels,
      this.presences,
      this.onlineUsers,
      this.channelHandlersSetUp,
      this.lastJoinAttempts,
      this.JOIN_DEBOUNCE_MS,
      this.notifyStatusChange.bind(this),
      this.connect.bind(this)
    );
  }

  /**
   *
   */
  leaveConversation(conversationId: string) {
    this.requiredConversationIds.delete(conversationId);
    leaveConversationImpl(
      conversationId,
      this.channels,
      this.channelHandlersSetUp,
      this.presences,
      this.onlineUsers,
      this.lastJoinAttempts
    );
  }

  /**
   *
   */
  joinGroupChannel(channelId: string): Channel | null {
    return joinGroupChannelImpl(this.socket, channelId, this.channels, this.connect.bind(this));
  }

  /**
   *
   */
  leaveGroupChannel(channelId: string) {
    leaveGroupChannelImpl(channelId, this.channels);
  }

  /**
   *
   */
  joinForum(forumId: string, callbacks?: ForumChannelCallbacks): Channel | null {
    return joinForumFT(this.state, forumId, callbacks);
  }

  /**
   *
   */
  leaveForum(forumId: string) {
    leaveForumFT(this.state, forumId);
  }

  /**
   *
   */
  subscribeToForum(forumId: string): Promise<{ subscribed: boolean }> {
    return subscribeToForumFT(this.state, forumId);
  }

  /**
   *
   */
  unsubscribeFromForum(forumId: string): Promise<{ subscribed: boolean }> {
    return unsubscribeFromForumFT(this.state, forumId);
  }

  /**
   *
   */
  joinThread(threadId: string, callbacks?: ThreadChannelCallbacks): Channel | null {
    return joinThreadFT(this.state, threadId, callbacks);
  }

  /**
   *
   */
  leaveThread(threadId: string) {
    leaveThreadFT(this.state, threadId);
  }

  /**
   *
   */
  voteOnThread(threadId: string, value: 1 | -1 | 0): Promise<ThreadVotePayload> {
    return voteOnThreadFT(this.state, threadId, value);
  }

  /**
   *
   */
  voteOnComment(
    threadId: string,
    commentId: string,
    value: 1 | -1 | 0
  ): Promise<CommentVotePayload> {
    return voteOnCommentFT(this.state, threadId, commentId, value);
  }

  /**
   *
   */
  sendComment(
    threadId: string,
    content: string,
    parentId?: string
  ): Promise<{ comment_id: string }> {
    return sendCommentFT(this.state, threadId, content, parentId);
  }

  /**
   *
   */
  sendThreadTyping(threadId: string, isTyping: boolean) {
    sendThreadTypingFT(this.state, threadId, isTyping);
  }

  /**
   *
   */
  getThreadViewers(threadId: string): Promise<{ viewers: ThreadViewerPayload[] }> {
    return getThreadViewersFT(this.state, threadId);
  }

  /**
   *
   */
  sendTyping(topic: string, isTyping: boolean) {
    sendTypingDebouncedImpl(topic, isTyping, this.channels);
  }

  /**
   *
   */
  sendReaction(
    conversationId: string,
    messageId: string,
    emoji: string,
    action: 'add' | 'remove'
  ): void {
    sendReactionImpl(conversationId, messageId, emoji, action, this.channels);
  }

  /**
   * Send a reaction on a group channel message via WebSocket.
   */
  sendGroupReaction(
    channelId: string,
    messageId: string,
    emoji: string,
    action: 'add' | 'remove'
  ): void {
    const topic = `group:${channelId}`;
    const channel = this.channels.get(topic);
    if (channel?.state === 'joined') {
      const eventName = action === 'add' ? 'add_reaction' : 'remove_reaction';
      channel.push(eventName, { message_id: messageId, emoji });
    }
  }

  /**
   *
   */
  getChannel(topic: string): Channel | undefined {
    return this.channels.get(topic);
  }

  /**
   *
   */
  async peekConversationsPresence(conversationIds: string[]): Promise<() => void> {
    return peekPresenceImpl(
      conversationIds,
      this.socket,
      this.channels,
      this.peekTimeouts,
      this.connect.bind(this),
      this.joinConversation.bind(this),
      this.leaveConversation.bind(this)
    );
  }
}
